import { z } from "zod";

/**
 * Geocode contract stubs (M0 ships types + the queue seam; M2 Geo Core
 * implements provider adapters and behavior). Shape mirrors M2.md /
 * M3.md's documented interfaces so M2/M3 don't need to reshape this later:
 * M3's `GeocodeRequest` DTO (`mode`, `rows[{row_key,query?,lat?,lng?}]`,
 * `country_default`) crosses into this batch request; M2's
 * `geocode(batch, opts) -> results[]` produces `GeocodeResult[]`.
 */

export const CURRENT_GEOCODE_SCHEMA_VERSION = 1;
export const SUPPORTED_GEOCODE_SCHEMA_VERSIONS = [1];

export const GeocodeRowInputSchema = z.object({
  row_key: z.string().min(1),
  query: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
});
export type GeocodeRowInput = z.infer<typeof GeocodeRowInputSchema>;

export const GeocodeBatchOptsSchema = z.object({
  tier: z.enum(["free", "paid"]).optional(),
  accountId: z.string().optional(),
  premiumOptIn: z.boolean().optional(),
  region: z.string().optional(),
});
export type GeocodeBatchOpts = z.infer<typeof GeocodeBatchOptsSchema>;

export const GeocodeBatchRequestSchema = z.object({
  schema_version: z.literal(CURRENT_GEOCODE_SCHEMA_VERSION),
  dataset_id: z.string().min(1),
  mode: z.enum(["coordinate", "address"]),
  rows: z.array(GeocodeRowInputSchema),
  country_default: z.string().min(1),
  opts: GeocodeBatchOptsSchema.optional(),
});
export type GeocodeBatchRequest = z.infer<typeof GeocodeBatchRequestSchema>;

export const GeocodeMatchTypeSchema = z.enum(["exact", "interpolated", "centroid", "none"]);
export type GeocodeMatchType = z.infer<typeof GeocodeMatchTypeSchema>;

export const GeocodeResultSchema = z.object({
  row_key: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  confidence: z.number().min(0).max(1),
  match_type: GeocodeMatchTypeSchema,
  normalized_input: z.string(),
  provider: z.string(),
  policy: z.enum(["shared_cacheable", "user_scoped"]),
  place_ref: z.string().optional(),
  needs_review: z.boolean(),
  attribution_required: z.boolean(),
});
export type GeocodeResult = z.infer<typeof GeocodeResultSchema>;

export const GeocodeBatchResponseSchema = z.object({
  schema_version: z.literal(CURRENT_GEOCODE_SCHEMA_VERSION),
  dataset_id: z.string(),
  job_id: z.string(),
  results: z.array(GeocodeResultSchema),
});
export type GeocodeBatchResponse = z.infer<typeof GeocodeBatchResponseSchema>;
